import DOMPurify from 'dompurify'
import { marked } from 'marked'
import type { Course, CourseProblem } from '../../types'

/** The problems the posting check found, from a 422 on publish or save; null for any other error. */
export const courseProblems = (error: unknown): CourseProblem[] | null => {
  const detail = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response?.data?.detail
  const problems = (detail as { problems?: unknown })?.problems
  return Array.isArray(problems) ? (problems as CourseProblem[]) : null
}

/** A course's address from its title: "Risk 101: Sizing" → "risk-101-sizing". */
export const slugify = (title: string): string =>
  title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)

/** The privacy-respecting embed for a YouTube or Vimeo link; null for anything else (shown as a link). */
export const videoEmbedUrl = (url: string | null | undefined): string | null => {
  if (!url) return null
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  const host = parsed.hostname.replace(/^www\.|^m\./, '')
  const id = (value: string | null | undefined) => (value && /^[\w-]{6,20}$/.test(value) ? value : null)
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const v = id(parsed.searchParams.get('v')) || id(parsed.pathname.match(/^\/(?:shorts|embed|live)\/([\w-]+)/)?.[1])
    return v ? `https://www.youtube-nocookie.com/embed/${v}` : null
  }
  if (host === 'youtu.be') {
    const v = id(parsed.pathname.slice(1))
    return v ? `https://www.youtube-nocookie.com/embed/${v}` : null
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const v = parsed.pathname.match(/(\d{5,12})/)?.[1]
    return v ? `https://player.vimeo.com/video/${v}` : null
  }
  return null
}

/** Lesson text (Markdown) as safe HTML. */
export const lessonHtml = (text: string): string => DOMPurify.sanitize(marked.parse(text, { async: false }) as string)

/** "Free", "Included with Premium or VIP", or "Not open yet" for a priced course. */
export const accessLine = (course: Pick<Course, 'access' | 'included_tier_ids'>, tierNames: Record<number, string>): string => {
  if (course.access === 'free') return 'Free'
  if (course.access === 'subscribers') {
    const names = course.included_tier_ids.map((id) => tierNames[id]).filter(Boolean)
    return names.length ? `Included with ${names.join(' or ')}` : 'For subscribers'
  }
  return 'Not open yet'
}

export const coursePath = (slug: string) => `/learn/${encodeURIComponent(slug)}`
export const lessonPath = (slug: string, lessonId: number) => `${coursePath(slug)}/lessons/${lessonId}`
export const COURSE_PATH = '/learn/:slug'
export const LESSON_PATH = '/learn/:slug/lessons/:lessonId'
export const STUDIO_PATH = '/courses'
export const CATALOG_PATH = '/learn'
export const CERTIFICATE_PATH = '/learn/certificates/:number'
export const certificatePath = (number: string) => `/learn/certificates/${encodeURIComponent(number)}`
export const studioCoursePath = (id: number) => `/courses/${id}`
